import { styled } from '@mui/material/styles';

export const filterItems = (items, query, categoryMapping) => {
    // Trim the query to remove leading and trailing spaces
    const trimmedQuery = query.trim().toLowerCase();

    return items.filter(item => {
        // Convert item fields to lowercase for case-insensitive comparison
        const itemName = item.item_name.toLowerCase();
        const itemDescription = item.description.toLowerCase();
        const categoryTitle = (categoryMapping[item.item_category] || "").toLowerCase();

        // Check if any of the item fields include the trimmed query
        return (
            itemName.includes(trimmedQuery) ||
            itemDescription.includes(trimmedQuery) ||
            categoryTitle.includes(trimmedQuery)
        );
    });
};

export const transformUnits = (quantity, measuring_unit) =>{
    if (measuring_unit === 'Kilograms') {
        quantity *= 1000;
        measuring_unit = 'Grams';
    }
    else if (measuring_unit === 'Liters') {
        quantity *= 1000;
        measuring_unit = 'Milliliters';
    }
    else if (measuring_unit === 'Meters') {
        quantity *= 100;
        measuring_unit = 'Centimeters';
    }
    return [quantity, measuring_unit];
}

export const formatDate = (dt) => {
    const yyyy = dt.getFullYear();
    let mm = dt.getMonth() + 1; // Months start at 0!
    let dd = dt.getDate();

    if (dd < 10) dd = '0' + dd;
    if (mm < 10) mm = '0' + mm;

    const formattedToday = yyyy + '/' + mm + '/' + dd;
    return formattedToday;
}

export const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export function timeout(delay) {
    return new Promise( res => setTimeout(res, delay) );
}

// Normalizes axios error shapes into a single displayable string/object so
// every catch block can show errors consistently instead of reimplementing
// this digging logic (String(error), error.message, error.response.data, ...).
export const getErrorMessage = (error) => {
    const data = error?.response?.data;
    if (data) {
        if (typeof data === 'string') return data;
        if (data.detail) return data.detail;
        if (data.message) return data.message;
        return data;
    }
    if (error?.message) return error.message;
    return String(error);
};


/**
 * The timezone the business runs in.
 *
 * Not the browser's. The kitchen is in Bengaluru, every figure the backend
 * produces is bucketed in IST, and a payslip dated from a laptop in another
 * timezone is simply dated wrong. This was got wrong twice: first with
 * `toISOString()`, which gave UTC and so returned *yesterday* before 05:30 IST;
 * then with browser-local, which is only correct while the laptop happens to
 * be in India. The machine this was written on runs on UTC+4, where local and
 * IST disagree for the last ninety minutes of every day.
 */
const BUSINESS_TIMEZONE = 'Asia/Kolkata';

/**
 * Format a Date as YYYY-MM-DD from its own calendar fields.
 *
 * A pure formatter with no conversion, for dates that were *constructed* as
 * calendar dates - "the first of this month" is already the answer and
 * converting it would move it.
 */
export function isoLocal(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Now, as a Date whose ordinary getters read the IST wall clock. */
function nowInBusinessTime() {
    return new Date(new Date().toLocaleString('en-US',
        { timeZone: BUSINESS_TIMEZONE }));
}

/** Today in the business's timezone. */
export function todayIso() {
    return isoLocal(nowInBusinessTime());
}

/** The first day of an N-day window ending today, today included. */
export function isoDaysAgo(days) {
    const date = nowInBusinessTime();
    date.setDate(date.getDate() - (days - 1));
    return isoLocal(date);
}

/** First and last day of the month `offset` months from this one. */
export function monthBounds(offset = 0) {
    const now = nowInBusinessTime();
    const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
    return { start: isoLocal(first), end: isoLocal(last) };
}

/**
 * The Indian financial year containing today, or one offset from it.
 *
 * April to March, which is what every filing and every accountant works to -
 * a trailing 365 days answers a different question entirely.
 */
export function financialYearBounds(offset = 0) {
    const now = nowInBusinessTime();
    const startYear = (now.getMonth() >= 3 ? now.getFullYear()
        : now.getFullYear() - 1) + offset;
    return {
        start: isoLocal(new Date(startYear, 3, 1)),
        end: isoLocal(new Date(startYear + 1, 2, 31)),
    };
}
